// Render warehouse option HTML
const renderWarehouseOption = (location, isActive, isMyWarehouse, distance) => {
  const cleanedName = cleanLocationName(location.name);
  const distanceText = distance !== null && distance !== undefined 
    ? `${distance.toFixed(1)} mi. away` 
    : '';
  
  const addressLines = [];
  if (location.address?.address1) addressLines.push(location.address.address1);
  if (location.address?.address2) addressLines.push(location.address.address2);
  if (location.address?.city && location.address?.provinceCode) {
    addressLines.push(`${location.address.city}, ${location.address.provinceCode} ${location.address.zip || ''}`.trim());
  }
  
  // Fallback to city and province if address lines aren't available
  if (addressLines.length === 0) {
    if (location.address?.city) {
      addressLines.push(location.address.city);
    }
    if (location.address?.provinceCode) {
      addressLines.push(location.address.provinceCode);
    }
  }

  // Build Google Maps URL from address
  const addressParts = [];
  if (location.address?.address1) addressParts.push(location.address.address1);
  if (location.address?.address2) addressParts.push(location.address.address2);
  if (location.address?.city) addressParts.push(location.address.city);
  if (location.address?.provinceCode) addressParts.push(location.address.provinceCode);
  if (location.address?.zipCode) addressParts.push(location.address.zipCode);
  
  const fullAddress = addressParts.join(', ');
  const googleMapsUrl = fullAddress 
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`
    : '#';

  return `
    <button class="warehouse-option ${isActive ? 'selected' : ''}" data-location-id="${location.id}">
      <div>
        <div class="warehouse-option__radio"></div>
      </div>
      <div class="warehouse-option__content">
        <div class="warehouse-option__header">
          <div class="warehouse-option__details">
            <div class="warehouse-option__name">${cleanedName}</div>
            ${distanceText ? `<div class="warehouse-option__distance">${distanceText}</div>` : ''}
          </div>
          ${isMyWarehouse ? '<div class="warehouse-option__badge">My Warehouse</div>' : ''}
        </div>
        <div class="warehouse-option__address">
          ${addressLines.map(line => `<div>${line}</div>`).join('')}
        </div>
        <a href="${googleMapsUrl}" target="_blank">Directions</a>
      </div>
    </button>
  `;
}

// Clear the cart
const clearCart = async () => {
  try {
    const response = await fetch('/cart/clear.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });

    if (response.ok) {
      // Dispatch cart refresh event to update cart UI
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      return true;
    }
    return false;
  } catch (error) {
    console.error('Error clearing cart:', error);
    return false;
  }
};

// Populate warehouse options
const populateWarehouseOptions = async () => {
  const warehouseOptionsContainer = document.querySelector('.warehouse-options');
  
  if (!warehouseOptionsContainer) {
    return;
  }

  const locationsData = getStoredLocationsData();
  if (!locationsData || !locationsData.length) {
    warehouseOptionsContainer.innerHTML = '<p>No warehouse locations available.</p>';
    return;
  }

  const selectedLocation = getSelectedLocation();
  const selectedLocationId = selectedLocation?.id;
  const modal = document.getElementById('warehouseSelectorModal');
  const preferredLocationLabel = modal?.dataset.preferredLocationLabel || null;

  // Render all options
  const optionsHTML = locationsData.map(location => {
    const cleanedName = cleanLocationName(location.name);
    let isActive;
    let isMyWarehouse = location.id === selectedLocationId;

    if (preferredLocationLabel) {
      isActive = cleanedName === preferredLocationLabel;
    } else {
      isActive = isMyWarehouse;
    }

    return renderWarehouseOption(location, isActive, isMyWarehouse, location.distance);
  }).join('');

  warehouseOptionsContainer.innerHTML = optionsHTML;

  if (preferredLocationLabel && modal) {
    delete modal.dataset.preferredLocationLabel;
  }
  
  // Add click handlers for warehouse option selection
  warehouseOptionsContainer.addEventListener('click', (e) => {
    // Find the closest warehouse-option button (in case click is on a child element)
    const warehouseOption = e.target.closest('.warehouse-option');
    
    // Don't handle clicks on the Directions link
    if (e.target.tagName === 'A' && e.target.href) {
      return;
    }
    
    if (warehouseOption) {
      // Remove selected class from all sibling buttons
      const siblings = warehouseOption.parentElement.querySelectorAll('.warehouse-option');
      siblings.forEach(sibling => sibling.classList.remove('selected'));
      
      // Add selected class to clicked button
      warehouseOption.classList.add('selected');
    }
  });
}

const initModal = () => {
  const modalContainer = document.querySelector('.modal-container');
  
  if (!modalContainer) {
    return;
  }

  document.addEventListener('click', async (e) => {
    // Close Modal
    if (e.target.matches('[data-action="close"]') || e.target.closest('.modal-panel__close-button')) {
      modalContainer.classList.remove('open');
    }

    // Select Warehouse
    if (e.target.matches('[data-action="selectWarehouse"]')) {
      // Find the selected warehouse option button
      const selectedOption = document.querySelector('.warehouse-option.selected');
      
      if (!selectedOption) {
        return;
      }
      
      // Get the location ID from the selected option
      const locationId = selectedOption.getAttribute('data-location-id');
      
      if (!locationId) {
        return;
      }
      
      // Get the current selected location
      const currentSelectedLocation = getSelectedLocation();
      
      // Check if the selected location is different from the current one
      if (currentSelectedLocation?.id === locationId) {
        // Same location, no need to update
        return;
      }
      
      // Find the full location data from stored locations
      const locationsData = getStoredLocationsData();
      if (!locationsData || !locationsData.length) {
        return;
      }
      
      const selectedLocationData = locationsData.find(loc => loc.id === locationId);
      
      if (selectedLocationData) {
        // Clear the cart
        await clearCart();

        // Populate warehouse details in button
        const warehouse = document.querySelector('.my-warehouse');
        const warehouseDetails = populateWarehouseDetails(selectedLocationData);
        warehouse.innerHTML = warehouseDetails;

        // Store the selected location
        storeSelectedLocation(selectedLocationData);
        modalContainer.classList.remove('open');
      }
    }
  });

  // Watch for when the 'open' class is added to the modal
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
        if (modalContainer.classList.contains('open')) {
          populateWarehouseOptions();
        }
      }
    });
  });

  // Start observing the modal for class changes
  observer.observe(modalContainer, {
    attributes: true,
    attributeFilter: ['class']
  });

  // Populate warehouse options initially
  populateWarehouseOptions();
};

// Wait for DOM to be ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initModal);
} else {
  initModal();
}
